import { useState } from "react";

import { type Building } from "@eisox/api/core-api";
import { ActionButton, CanAccess, IconButton } from "@eisox/design-system";
import { MapIcon, PencilIcon, RefreshIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { floorApi, gatewayApi, roomApi } from "~/api";
import { MoveItemDialog } from "~/components/move-item-dialog";
import { m } from "~/paraglide/messages";
import type { Permission } from "~/types/auth.type";
import type { EnhancedValve } from "~/types/valve.type";

import { canAccessEditValveDrawer } from "../helpers";
import { EditValveDrawer } from "./edit-valve-drawer";
import { ReplaceValveDialog } from "./replace-valve-dialog";

const ReplaceValveTrigger: React.FC<{ valve: EnhancedValve; valves: EnhancedValve[]; isMobile: boolean }> = ({
  valve,
  valves,
  isMobile,
}) => {
  const { buildingId } = useParams({ strict: false });
  const [open, setOpen] = useState(false);
  return (
    <>
      {isMobile ? (
        <IconButton disabled={!valve.mac} onClick={() => setOpen(true)}>
          <RefreshIcon style={{ transform: "rotate(90deg)" }} />
        </IconButton>
      ) : (
        <ActionButton disabled={!valve.mac} onClick={() => setOpen(true)}>
          {m.replace()} <RefreshIcon style={{ transform: "rotate(90deg)" }} />
        </ActionButton>
      )}
      {open && buildingId && (
        <ReplaceValveDialog open={open} onOpenChange={setOpen} buildingId={buildingId} valve={valve} valves={valves} />
      )}
    </>
  );
};

const MoveValveTrigger: React.FC<{
  building: Building;
  valves: EnhancedValve[];
  valveId: string;
  isMobile: boolean;
  disabled: boolean;
}> = ({ building, valves, valveId, isMobile, disabled }) => {
  const [open, setOpen] = useState(false);
  const { data: floors = [] } = useQuery(floorApi.queries.building(building.id));
  const { data: rooms = [] } = useQuery(roomApi.queries.building(building.id));
  const { data: gateways = [] } = useQuery(gatewayApi.queries.building(building.id));
  return (
    <>
      {isMobile ? (
        <IconButton disabled={disabled} onClick={() => setOpen(true)}>
          <MapIcon />
        </IconButton>
      ) : (
        <ActionButton disabled={disabled} onClick={() => setOpen(true)}>
          {m.move()} <MapIcon />
        </ActionButton>
      )}
      {open && (
        <MoveItemDialog
          open={open}
          onOpenChange={setOpen}
          type="valve"
          buildingId={building.id}
          items={valves}
          floors={floors}
          rooms={rooms}
          gateways={gateways}
          defaultSelectedItemId={valveId}
        />
      )}
    </>
  );
};

interface ValveDetailActionsProps {
  building: Building;
  valve: EnhancedValve;
  valves: EnhancedValve[];
  permissions: Permission;
}

export const ValveDetailActions: React.FC<ValveDetailActionsProps> = ({ building, valve, valves, permissions }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="gap-2.5 mt-auto flex justify-center [&_button]:flex-1 [&_button]:justify-center [&_button]:bg-primary-white [&_button]:text-primary-dark-gray">
      <EditValveDrawer valve={valve} building={building} permissions={permissions}>
        {isMobile ? (
          <IconButton>
            <PencilIcon />
          </IconButton>
        ) : (
          <ActionButton disabled={!canAccessEditValveDrawer(permissions)}>
            {m.edit()} <PencilIcon />
          </ActionButton>
        )}
      </EditValveDrawer>

      <CanAccess isAuthorized={permissions.valve?.mac?.update}>
        <ReplaceValveTrigger valve={valve} valves={valves} isMobile={isMobile} />
      </CanAccess>

      <CanAccess isAuthorized={permissions.valve?.gatewayId?.update && !isMobile}>
        <MoveValveTrigger
          building={building}
          valves={valves}
          valveId={valve.id}
          isMobile={isMobile}
          disabled={!valve.mac}
        />
      </CanAccess>
    </div>
  );
};

ValveDetailActions.displayName = "ValveDetailActions";
